import { Actie, TvvAfschalingBesteldeRouteActie, TvvWijzigBesteldeRouteActie, useGetDossierActiesById } from "@/entities/actie";
import { useGetStationDisplayName } from "@/entities/station";
import { getRouteStationNames, TvvBesteldeRoute, TVVBestelStatus, useBevestigStopTvvRoute } from "@/entities/tvv";
import { RouteTimeline, TvvMessageBar, TvvRouteItemBase } from "@/features/tvv/components/shared";
import { Button, ErrorDisplay, ProgressCircleWithLabel, Separator } from "@/shared/components";
import { StationEtaSection } from "./station-eta-section";

type RegieCentrumBussenRouteItemProps = {
    route: TvvBesteldeRoute;
    dossierId: string;
    defaultOpen?: boolean;
};

type TvvWijzigingOfAfschalingActie = TvvWijzigBesteldeRouteActie | TvvAfschalingBesteldeRouteActie;

function createTvvBestellingGewijzigdForRouteSelector(besteldeRouteId: number) {
    return (acties: Actie[]): TvvWijzigingOfAfschalingActie | undefined => {
        return acties.find(
            (actie): actie is TvvWijzigingOfAfschalingActie =>
                (actie.type === "TvvWijzigBesteldeRoute" || actie.type === "TvvAfschalingBesteldeRoute") &&
                actie.besteldeRouteId === besteldeRouteId
        );
    };
}

export function RegieCentrumBussenRouteItem({ route, dossierId, defaultOpen = false }: Readonly<RegieCentrumBussenRouteItemProps>) {
    const getStationDisplayName = useGetStationDisplayName();
    const { data: tvvActie } = useGetDossierActiesById(dossierId, {
        select: createTvvBestellingGewijzigdForRouteSelector(route.id),
    });
    const { beginStation, eindStation } = getRouteStationNames(route.stationCodes, getStationDisplayName);
    const beginStationCode = TvvBesteldeRoute.getBeginStationCode(route);
    const eindStationCode = TvvBesteldeRoute.getEindStationCode(route);
    const beginStationBussen = TvvBesteldeRoute.getBussenForStation(route, beginStationCode);
    const eindStationBussen = TvvBesteldeRoute.getBussenForStation(route, eindStationCode);
    const bussenOpEindStation = TvvBesteldeRoute.getBussenOpEindStation(route);
    const actieveBussen = TvvBesteldeRoute.actieveAndAfgeschaaldeBussen(route)[0].length;
    const isComplete = TvvBesteldeRoute.isComplete(route);
    const routeEditable = TvvBesteldeRoute.routeEditable(route);
    const moetAfgeschaaldWorden = TvvBesteldeRoute.moetAfgeschaaldWorden(route);
    const progressCircleType = moetAfgeschaaldWorden ? "exclamation" : isComplete ? "check" : "default";
    const { mutate: bevestigStop, isPending, error } = useBevestigStopTvvRoute();

    const bevestigStopTvvRoute = () => {
        bevestigStop({ dossierId, tvvId: route.id });
    };

    return (
        <TvvRouteItemBase route={route} defaultOpen={defaultOpen} summaryText={`${actieveBussen}/${route.bussenBesteld} geleverd`}>
            <RouteTimeline stationCodes={route.stationCodes} rijtijden={route.rijtijden} />

            <TvvMessageBar dossierId={dossierId} status={route.status} actie={tvvActie} />

            {route.status === TVVBestelStatus.StopAangevraagd && (
                <Button onClick={bevestigStopTvvRoute} disabled={isPending}>
                    TVV route stoppen
                </Button>
            )}
            {error && <ErrorDisplay error={error} title="Fout bij het stoppen van route" />}

            <div className="rounded-lg border p-4">
                <ProgressCircleWithLabel
                    total={route.bussenBesteld}
                    current={actieveBussen}
                    label="Geleverd"
                    unit="bussen"
                    type={progressCircleType}
                />
            </div>

            <div className="rounded-lg border p-4">
                <div className="grid grid-cols-[1fr_min-content_1fr] gap-4" data-testid="stations-etas">
                    <StationEtaSection
                        stationCode={beginStationCode}
                        stationNaam={beginStation}
                        aantalBussen={route.bussenBesteld}
                        aantalBussenStation={route.aantalBussenOpBeginStation}
                        bussen={beginStationBussen}
                        route={route}
                        dossierId={dossierId}
                        isRouteComplete={isComplete}
                        canAddOrEditEtas={routeEditable}
                        data-testid="etas-beginstation"
                    />
                    <Separator orientation="vertical" className="border-default border" />
                    <StationEtaSection
                        stationCode={eindStationCode}
                        stationNaam={eindStation}
                        aantalBussen={route.bussenBesteld}
                        aantalBussenStation={bussenOpEindStation}
                        bussen={eindStationBussen}
                        route={route}
                        dossierId={dossierId}
                        isRouteComplete={isComplete}
                        canAddOrEditEtas={routeEditable}
                        data-testid="etas-eindstation"
                    />
                </div>
            </div>
        </TvvRouteItemBase>
    );
}
